import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { keyName } from '@bingads-webui/keycode';

function isInteger(n) {
  return n === parseInt(n, 10);
}

/**
 * A pagination control with previous, next and jump to ramdon page functionabilities
 */

export class Pagination extends PureComponent {
  static defaultProps = {
    pageSize: 20,
    itemCount: 0,
    previousIconClassName: 'glyphicon glyphicon-triangle-left',
    nextIconClassName: 'glyphicon glyphicon-triangle-right',
    pageText: 'Page: ',
    totalPageCountText: ' of ',
    previousPageText: 'Previous page',
    nextPageText: 'Next page',
    enableRandomPage: true,
  };

  state = {
    currentInputValue: (this.props.pageNumber + 1).toString(),
    prevPropsPageNumber: this.props.pageNumber,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.pageNumber !== prevState.prevPropsPageNumber) {
      return {
        currentInputValue: (nextProps.pageNumber + 1).toString(),
        prevPropsPageNumber: nextProps.pageNumber,
      };
    }
    return null;
  }

  get pageCount() {
    return Math.ceil(this.props.itemCount / this.props.pageSize);
  }

  get isFirstPage() {
    return this.props.pageNumber <= 0;
  }

  get isLastPage() {
    return this.props.pageNumber >= this.pageCount - 1;
  }

  runIfEnterNext = (e) => {
    this.runIfEnter(e, this.incPageNumber);
  }

  runIfEnterPrev = (e) => {
    this.runIfEnter(e, this.decPageNumber);
  }

  runIfEnter = (event, callback, ...restParams) => {
    if (event.key === keyName[13]) {
      event.preventDefault();
      callback(...restParams);
    }
  }

  goToPage = (value) => {
    const pageNumber = Math.max(Math.min(value, this.pageCount - 1), 0);

    this.setState({ currentInputValue: (pageNumber + 1).toString() });
    this.props.onPageNumberChanged(pageNumber);
  }

  decPageNumber = () => {
    this.goToPage(this.props.pageNumber - 1);
  }

  incPageNumber = () => {
    this.goToPage(this.props.pageNumber + 1);
  }

  runIfEnterUnfocus = (e) => {
    this.runIfEnter(e, () => { this.textInput.blur(); });
  }

  updatePageNumber = (event) => {
    const inputValue = event.target.value;
    const intValue = this.props.i18n.parseDecimal(inputValue) - 1;

    if (isInteger(intValue)) {
      this.goToPage(intValue);
    } else {
      this.setState({
        currentInputValue: (this.props.pageNumber + 1).toString(),
      });
    }
  }

  render() {
    return (
      <div className="ba-pagination">
        {/* TODO [lizho] Add common <Button> to handle similar logic */}
        <button
          className="btn btn-default prev"
          type="button"
          onClick={this.decPageNumber}
          onKeyPress={this.runIfEnterPrev}
          disabled={this.isFirstPage}
          tabIndex={this.isFirstPage ? '-1' : '0'}
          aria-label={this.props.previousPageText}
        >
          <div className={this.props.previousIconClassName} />
        </button>

        <div className="text">
          <span>{this.props.pageText}</span>
        </div>

        <input
          className="pager-input"
          type="text"
          ref={(input) => { this.textInput = input; }}
          value={this.state.currentInputValue}
          onChange={event => this.setState({ currentInputValue: event.target.value })}
          onBlur={this.updatePageNumber}
          onKeyPress={this.runIfEnterUnfocus}
          maxLength={Math.floor(this.pageCount.toString().length)}
          disabled={!this.props.enableRandomPage}
          aria-label={this.props.pageText || 'Page'}
        />

        <div className="text">
          <span>{this.props.totalPageCountText}</span>
          <span className="pager-totalCount">{this.pageCount}</span>
        </div>

        <button
          className="btn btn-default next"
          type="button"
          onClick={this.incPageNumber}
          onKeyPress={this.runIfEnterNext}
          disabled={this.isLastPage}
          tabIndex={this.isLastPage ? '-1' : '0'}
          aria-label={this.props.nextPageText}
        >
          <div className={this.props.nextIconClassName} />
        </button>
      </div>
    );
  }
}

Pagination.propTypes = {
  /**
   * item count per page
   */
  pageSize: PropTypes.number,
  /**
   * page number
   */
  pageNumber: PropTypes.number.isRequired,
  /**
   * item count
   */
  itemCount: PropTypes.number,
  /**
   * className used for previous icon
   */
  previousIconClassName: PropTypes.string,
  /**
   * Callback when page number is changed
   * @param {number} pageNumber - new page number
   */
  onPageNumberChanged: PropTypes.func.isRequired,
  /**
   * Text like 'Page' in English
   */
  pageText: PropTypes.string,
  /**
   * Text like ' of: ' in English
   */
  totalPageCountText: PropTypes.string,
  /**
   * className used for next icon
   */
  nextIconClassName: PropTypes.string,
  /**
   * text used for previous button accessibility
   */
  previousPageText: PropTypes.string,
  /**
   * text used for next button accessibility
   */
  nextPageText: PropTypes.string,
  /**
   * whether or not enable jumping to random page.
   */
  enableRandomPage: PropTypes.bool,
  /**
   * i18n for localization
   */
  i18n: PropTypes.shape({
    parseDecimal: PropTypes.func.isRequired,
  }).isRequired,
};
